import { useEffect, useState } from "react";
import { api, currency } from "../lib/api";
import { PageHeader } from "../components/Bits";
import { Card } from "../components/ui/card";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar, Cell } from "recharts";

export default function Reports() {
  const [sales, setSales] = useState(null);
  const [prod, setProd] = useState(null);
  const [dur, setDur] = useState(null);

  useEffect(() => {
    api.get("/reports/sales", { params: { days: 30 } }).then((r) => setSales(r.data));
    api.get("/reports/production").then((r) => setProd(r.data));
    api.get("/reports/stage-duration", { params: { days: 60 } }).then((r) => setDur(r.data));
  }, []);

  return (
    <div className="p-8">
      <PageHeader eyebrow="Analiz" title="Raporlar" subtitle="Son 30 gün satış ve üretim performansı" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <Card className="p-5 border-slate-200">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Toplam Gelir (30g)</div>
          <div className="font-display text-3xl font-extrabold text-slate-900 mt-2">{sales ? currency(sales.total_revenue) : "-"}</div>
        </Card>
        <Card className="p-5 border-slate-200">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Sipariş Sayısı (30g)</div>
          <div className="font-display text-3xl font-extrabold text-slate-900 mt-2">{sales?.orders_count ?? "-"}</div>
        </Card>
        <Card className="p-5 border-slate-200">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Ort. Sepet</div>
          <div className="font-display text-3xl font-extrabold text-slate-900 mt-2">
            {sales && sales.orders_count ? currency(sales.total_revenue / sales.orders_count) : "-"}
          </div>
        </Card>
      </div>

      <Card className="p-6 border-slate-200 mb-6">
        <h3 className="font-display text-lg font-bold text-slate-900 mb-4">Günlük Gelir Trendi</h3>
        <div className="h-72 min-h-[280px]">
          <ResponsiveContainer>
            <AreaChart data={sales?.daily || []}>
              <defs>
                <linearGradient id="grev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2563EB" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#2563EB" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748b" }} />
              <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
              <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }} formatter={(v) => currency(v)} />
              <Area type="monotone" dataKey="revenue" stroke="#2563EB" strokeWidth={2} fill="url(#grev)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6 border-slate-200">
          <h3 className="font-display text-lg font-bold text-slate-900 mb-4">En Çok Satan Ürünler</h3>
          <div className="h-72 min-h-[280px]">
            <ResponsiveContainer>
              <BarChart data={sales?.top_products || []} layout="vertical" margin={{ left: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis type="number" tick={{ fontSize: 11, fill: "#64748b" }} />
                <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11, fill: "#64748b" }} />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }} formatter={(v) => currency(v)} />
                <Bar dataKey="revenue" fill="#2563EB" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6 border-slate-200">
          <h3 className="font-display text-lg font-bold text-slate-900 mb-4">Üretim Aşamalarında Aktif Sipariş</h3>
          <div className="h-72 min-h-[280px]">
            <ResponsiveContainer>
              <BarChart data={prod?.stages || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="stage" tick={{ fontSize: 10, fill: "#64748b" }} interval={0} angle={-25} textAnchor="end" height={70} />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }} />
                <Bar dataKey="active" fill="#2563EB" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <Card className="p-6 border-slate-200 mt-6">
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Son 60 gün · Darboğaz Analizi</div>
            <h3 className="font-display text-lg font-bold text-slate-900">Aşama Başına Ortalama Süre</h3>
          </div>
          <div className="text-xs text-slate-500">Uzun süren aşamalar darboğazı gösterir</div>
        </div>
        <div className="h-72 min-h-[280px]">
          <ResponsiveContainer>
            <BarChart data={dur?.stages || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="stage" tick={{ fontSize: 10, fill: "#64748b" }} interval={0} angle={-25} textAnchor="end" height={70} />
              <YAxis tick={{ fontSize: 11, fill: "#64748b" }} label={{ value: "saat", angle: -90, position: "insideLeft", style: { fontSize: 11, fill: "#64748b" } }} />
              <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }}
                formatter={(v, _n, item) => [`${v} saat (${(v/24).toFixed(1)} gün) · ${item.payload.sample_size} sipariş`, "Ortalama"]} />
              <Bar dataKey="avg_hours" radius={[6, 6, 0, 0]}>
                {(dur?.stages || []).map((s) => <Cell key={s.stage} fill={s.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
          {(dur?.stages || []).map((s) => (
            <div key={s.stage} className="flex items-center gap-2 border border-slate-200 rounded-lg px-3 py-2 bg-white">
              <span className="h-3 w-3 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
              <div className="min-w-0">
                <div className="text-xs font-semibold text-slate-800 truncate">{s.stage}</div>
                <div className="text-[11px] text-slate-500">
                  {s.avg_hours < 24 ? `${s.avg_hours}sa` : `${s.avg_days}g`} · {s.sample_size} sip
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
